import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { longestEmptyParagraphRun, readDocument } from "@/office/word/document";
import { readLayout } from "@/office/word/layout";

export const lektionUmbrueche: Lesson = {
  id: "umbrueche",
  title: "Umbrüche statt Leerzeilen",
  summary: "Eine neue Seite beginnen, ohne zwanzig Mal Enter zu drücken.",
  durationMinutes: 9,
  learningGoals: [
    "Den Seitenumbruch als eigenes Werkzeug kennen",
    "Abschnittsumbrüche und ihren Zweck verstehen",
    "Leerzeilen als Seitenfüller vermeiden",
  ],
  steps: [
    {
      id: "seitenumbruch",
      title: "Der Seitenumbruch",
      explanation: [
        "Das Muster aus dem letzten Modul wiederholt sich eine Ebene höher. Soll ein " +
          "Kapitel auf einer neuen Seite beginnen, drücken viele so oft Enter, bis der " +
          "Text umspringt.",
        "Und wieder hält das nur bis zur nächsten Änderung: Wird oben ein Satz ergänzt, " +
          "rutscht alles nach unten und die Überschrift steht plötzlich in der Mitte der " +
          "übernächsten Seite. Bei einem längeren Bericht sammeln sich so schnell " +
          "Dutzende Leerzeilen an, die niemand mehr zuordnen kann.",
        "Der Seitenumbruch macht es richtig: Er sagt Word „ab hier neue Seite“, unabhängig " +
          "davon, wie viel Text darüber steht. Du fügst ihn mit Strg + Enter ein oder über " +
          "„Einfügen“ → „Seitenumbruch“.",
      ],
      keyPoints: [
        "Strg + Enter fügt einen Seitenumbruch ein.",
        "Ein Seitenumbruch verschiebt sich mit dem Text – Leerzeilen nicht.",
        "Noch eleganter: In der Formatvorlage „Überschrift 1“ lässt sich „Seitenumbruch oberhalb“ dauerhaft einstellen.",
      ],
      task: {
        instruction:
          "Räume dein Dokument auf: Es darf keine Stelle mehr geben, an der zwei oder mehr " +
          "Leerzeilen hintereinander stehen. Nutze stattdessen einen Seitenumbruch.",
        hint: "Blende mit Strg + Umschalt + 8 die Steuerzeichen ein – der Seitenumbruch erscheint dann als gestrichelte Linie.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const laengsteKette = longestEmptyParagraphRun(snapshot);
          if (laengsteKette >= 2) {
            return fail(
              `Es gibt noch eine Stelle mit ${laengsteKette} Leerzeilen hintereinander.`,
              "Lösche die Leerzeilen und setze an ihre Stelle einen Seitenumbruch mit Strg + Enter.",
            );
          }
          return pass("Keine Leerzeilenketten mehr – dein Dokument bleibt jetzt stabil.");
        },
      },
    },
    {
      id: "abschnittsumbruch",
      title: "Abschnittsumbrüche",
      explanation: [
        "Ein Abschnitt ist ein Bereich des Dokuments mit eigenem Seitenlayout. Erst " +
          "dadurch wird möglich, was sonst nicht geht: eine einzelne Seite quer stellen, " +
          "ab der Einleitung mit der Seitenzählung neu beginnen oder für ein Kapitel " +
          "andere Seitenränder verwenden.",
        "Ohne Abschnittsumbruch gelten Ausrichtung, Ränder, Kopf- und Fußzeile immer für " +
          "das gesamte Dokument. Deshalb ist der Abschnittsumbruch die Voraussetzung für " +
          "fast alles, was in diesem Modul noch kommt.",
        "Du findest ihn unter „Layout“ → „Umbrüche“. Dort stehen zwei Gruppen: oben die " +
          "Seitenumbrüche, unten die Abschnittsumbrüche. „Nächste Seite“ ist der " +
          "Normalfall – er beginnt gleichzeitig einen neuen Abschnitt und eine neue Seite.",
      ],
      keyPoints: [
        "Ein Abschnitt kann eigene Ränder, Ausrichtung und Kopf-/Fußzeilen haben.",
        "Layout → Umbrüche → Abschnittsumbrüche → „Nächste Seite“.",
        "Abschnittsumbrüche sparsam einsetzen – jeder zusätzliche will gepflegt werden.",
      ],
      task: {
        instruction: "Füge deinem Dokument einen Abschnittsumbruch hinzu, sodass es aus zwei Abschnitten besteht.",
        hint: "Cursor an die gewünschte Stelle setzen, dann Layout → Umbrüche → „Nächste Seite“.",
        check: async () => {
          const layout = await readLayout();
          return fromCriteria(
            [
              criterion(
                `Das Dokument besteht aus mindestens zwei Abschnitten (gefunden: ${layout.sections.length})`,
                layout.sections.length >= 2,
              ),
            ],
            {
              success: `Dein Dokument hat jetzt ${layout.sections.length} Abschnitte – damit kannst du gleich weiterarbeiten.`,
              failure: "Es wurde erst ein Abschnitt gefunden.",
              hint: "Achte darauf, unter „Umbrüche“ wirklich einen Abschnittsumbruch zu wählen und nicht den Seitenumbruch darüber.",
            },
          );
        },
      },
    },
  ],
};
